import React, { useCallback, useState } from 'react';
import styled from 'styled-components';
import {SortableHandle} from 'react-sortable-hoc';

import Icon from '../../components/Icon';
import Notification from '../../components/Notification';
import Menu from '../../components/Menu';
import Button from '../../components/Button';

import DeletableCard from './DeletableCard';
import HeaderElementForm from './HeaderElementForm';
import DescriptionElementForm from './DescriptionElementForm';
import CalculationElementForm from './CalculationElementForm';
import AggregationElementForm from './AggregationElementForm';
import SubConfiguration from './SubConfiguration';
import JsonLogicBuilder from '../../components/JsonLogicBuilder';
import { getLogicBuilderVariables } from './shared';
import { Checkbox } from './QuestionBuilder';

function ElementBuilder({form, catalog, element, onChange, onDelete, copyFormElement}) {
  const [optionsOpen, setOptionsOpen] = useState(false);

  const handleDelete = useCallback(() => {
    onDelete(element);
  }, [onDelete, element]);

  const [menuOpen, toggleMenu] = useState(null);
  const [isContextChecked, toggleContext] = useState(element.context);
  const [isConditionChecked, toggleIsConditionChecked] = useState(element.condition);
  const [condition, editCondition] = useState(element.condition);

  function setContext() {
    element.context = !isContextChecked;
    isContextChecked ? toggleContext(false) : toggleContext(true);
  }

  const handleMenuToggle = useCallback(menuName => menuName ? toggleMenu(menuName) : toggleMenu(null), []);

  function setIsConditionChecked() {
    element.condition = null;
    toggleIsConditionChecked(!isConditionChecked);
  }

  function setEditCondition(conditionInput) {
    element.condition = conditionInput;
    editCondition(conditionInput);
  }

  function toggleElementOptions() {
   setOptionsOpen(!optionsOpen)
  }

  const iconId = "insight-about-icon-" + element.id;
  return (
    <DeletableCard
      focusable
      disabled={false}
      dialogTitle={`Delete this ${element.type.toLowerCase()} element?`}
      dialogDescription="Are you sure you want to delete this element? This cannot be undone."
      ok="Delete"
      onOk={handleDelete}>
      <Header>
        <DragIcon>
          <Icon name="align-justify" size="1.5em" />
        </DragIcon>
        <h4>{element.type.toLowerCase()}</h4>
        {element.type === 'DESCRIPTION' &&
          <>
            <InfoButton id={iconId} tertiary onClick={() => handleMenuToggle('info')}>
              <Icon name="alert-circle" />
            </InfoButton>
            <AboutMenu relativeTo={iconId} open={menuOpen === 'info'} onClose={handleMenuToggle}>
              <Menu.Text>This element accepts markdown.</Menu.Text>
            </AboutMenu>
          </>
        }

        <ElementOptionsButton id={`element-options-${element.id}`} tertiary onClick={() => toggleElementOptions()}>
          <Icon name="chevron-down" />
        </ElementOptionsButton>
        <Menu open={optionsOpen} relativeTo={`element-options-${element.id}`} onClose={() => toggleElementOptions()}>
          <Menu.Option onClick={() => copyFormElement(element)}>Copy Element</Menu.Option>
        </Menu>
      </Header>
      <Body>
        <ElementForm form={form} catalog={catalog} element={element} onChange={onChange} />
      <Checkbox>
        <input
          disabled={false}
          type="checkbox"
          checked={element.context}
          onChange={e => setContext()}
        />
        &nbsp;
        Show on setup
      </Checkbox>
      <Checkbox>
        <input
          disabled={false}
          type="checkbox"
          checked={!!isConditionChecked}
          onChange={e => setIsConditionChecked()}
        />
        &nbsp;
        Only show this element if...
      </Checkbox>
          {isConditionChecked && (
            <SubConfiguration>
              <SubConfiguration.Item>
                <JsonLogicBuilder
                  expand
                  variables={getLogicBuilderVariables(form, catalog)}
                  value={typeof condition === 'string' ? JSON.parse(condition) : null}
                  onChange={condition => setEditCondition(JSON.stringify(condition))}
                />
              </SubConfiguration.Item>
            </SubConfiguration>
          )}
      </Body>
    </DeletableCard>
  );
}

function ElementForm({ form, catalog, element, onChange }) {
  switch (element.type) {
    case 'HEADER':
      return <HeaderElementForm element={element} onChange={onChange} />;
    case 'DESCRIPTION':
      return <DescriptionElementForm element={element} onChange={onChange} />;
    case 'CALCULATION':
      return <CalculationElementForm form={form} catalog={catalog} element={element} onChange={onChange} />;
    case 'AGGREGATION':
      return <AggregationElementForm form={form} catalog={catalog} element={element} onChange={onChange} />;
    default:
      return <Notification error>Unknown element type: {element.type}</Notification>
  }
}

const Header = styled.div`
  display: flex;
  align-items: center;

  h4 { 
    flex-grow: 1;
    margin: 0.4rem 0 0 0;
    padding: 0;
    font-size: 1.125rem;
    text-transform: capitalize;
  }
`;

const ElementOptionsButton = styled(Button)`
  margin-right: -1rem;
  margin-left: 1rem;
`;

const DragIcon = SortableHandle(styled.div`
  cursor: grab;
  color: #b4b4b4;
  margin-top: 0.4rem;
  margin-right: 1rem;
`);

const Body = styled.div`
  margin: 1rem 0 0 2.5rem;

  > * {
    margin-top: 0.625rem;
  }

  textarea {
    resize: vertical;
  }
`;

export const AboutMenu = styled(Menu)`
  max-width: 300px;
`;

const InverseButton = styled(Button)`
  color: black;
`;

const InfoButton = styled(InverseButton)`
color: blue;
  margin-right: -1rem;
`;

export default ElementBuilder;